<template>
  <div class="row">
    <div class="col-12 p-0">
      <Card class="links-checker">
        <div class="links-checker__header">
          <span class="links-checker__title">
            {{ $t('linksChecker.title') }}
          </span>
          <span class="tw-mt-8">{{ $t('linksChecker.description') }}</span>
        </div>

        <div class="links-checker__controls">
          <UiInput
            v-model="link"
            label="URL"
            placeholder="URL"
            autofocus
            error-clear-on-change
          />
          <div class="tw-ml-8">
            <UiButton
              :disabled="checkingLink || !link.trim()"
              class="btn btn-primary"
              @click="handleCheckLink"
            >
              {{ $t('linksChecker.checkUrl') }}
            </UiButton>
          </div>
        </div>

        <div
          v-if="checkResult"
          class="tw-mt-16"
        >
          <div
            class="links-checker__result"
            :class="{
              'links-checker__result--success': checkResult.status === 'success',
              'links-checker__result--error': checkResult.status === 'error'
            }"
          >
            <i class="links-checker__result-icon fas fa-exclamation-circle" />
            <span>{{ checkResultMessage }}</span>
          </div>
        </div>
      </Card>
    </div>
  </div>
</template>

<script>
import { computed, ref } from 'vue';
import { onBeforeRouteLeave } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useApi } from '@/lib/request';
import { get } from '@/lib/lodash';

export default {
  name: 'LinksChecker',
  setup() {
    const link = ref('');
    const checkingLink = ref(false);
    const checkResult = ref(null);
    const { api } = useApi();
    const { t } = useI18n();

    const resetState = () => {
      link.value = '';
      checkingLink.value = false;
      checkResult.value = null;
    };

    const handleCheckLink = async () => {
      if (!link.value.trim()) {
        return;
      }

      checkingLink.value = true;
      checkResult.value = null;

      api.mediaBuyingLinksChecker({ url: link.value.trim() })
        .then((res) => {
          const { is_valid, error } = res.payload || {};

          if (is_valid) {
            checkResult.value = {
              status: 'success',
            };
          } else if (error?.code) {
            checkResult.value = {
              status: 'error',
              error,
            };
          }
        })
        .catch((err) => {
          const message = get(err, ['response', 'data', 'errors', 'url', 0, 'message']);

          if (message) {
            checkResult.value = {
              status: 'error',
              message,
            };
          }
        })
        .finally(() => checkingLink.value = false);
    };

    onBeforeRouteLeave(() => {
      resetState();
    });

    const checkResultMessage = computed(() => {
      if (!checkResult.value) {
        return '';
      }

      if (checkResult.value.message) {
        return checkResult.value.message;
      }

      if (checkResult.value.status === 'success') {
        return t('linksChecker.errors.fine');
      }

      if (checkResult.value.status === 'error' && checkResult.value.error?.code) {
        const errorMessageMap = {
          cannot_be_defined: () => t('linksChecker.errors.cannot_be_defined'),
          blacklisted: () => t('linksChecker.errors.blacklisted'),
          used_by_user: (error) => `${t('linksChecker.errors.used_by_user')}: ${error.campaign_incrementing_id}`,
          used_by_others: () => t('linksChecker.errors.used_by_others'),
        };

        const resolveMessage = errorMessageMap[checkResult.value.error.code];

        if (resolveMessage) {
          return resolveMessage(checkResult.value.error);
        }
      }

      return '';
    });

    return {
      link,
      checkingLink,
      checkResult,
      handleCheckLink,
      checkResultMessage,
    };
  },
};
</script>


<style lang="scss">
.links-checker {
  padding: 0;
  margin-left: 15px;
  margin-right: 15px;
  min-height: calc(100vh - 100px);

  &__header {
    display: flex;
    flex-direction: column;
    margin: 0;
  }

  &__title {
    font-size: 16px;
    font-weight: 600;
  }

  &__controls {
    margin-top: 16px;
    display: flex;
    align-items: flex-end;
  }

  &__result {
    display: flex;
    align-items: center;
    padding: 12px 16px;
    font-size: 14px;
    justify-self: flex-start;
    height: 32px;
    border-radius: 8px;

    &--success {
      background-color: var(--color-bg-comment-r);
    }

    &--error {
      background-color: var(--color-bg-comment-l);
    }
  }

  &__result-icon {
    margin-right: 8px;

    .links-checker__result--success & {
      color: var(--color-text-green);
    }

    .links-checker__result--error & {
      color: var(--color-text-red);
    }
  }
}
</style>
